import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import PropTypes from "prop-types";
import { memo, useMemo } from "react";

import { ModalTransition } from "../../../../components/shared/Dialog/Config";
import { canShowClaimAction } from "../../../../components/request/requestAssignmentDisplay";
import FileThumbnailGrid from "../../../../components/shared/files/FileThumbnailGrid";
import { getApiOrigin } from "../../../../../infrastructure/utils/resolveUploadUrl";
import { toJalaliDate } from "../../../../middleware/middleware";
import { getApplicantIdentityFields } from "../../shared/checkNewRequest/checkNewRequestPageUtils";
import { resolveApplicantMobile } from "../all-requests/allRequestsPageUtils";

const ACCORDION_SX = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1.5,
  bgcolor: "background.paper",
  "&:before": { display: "none" },
  mb: 1.5,
  boxShadow: "none",
};

const hasText = (value) =>
  value != null && String(value).trim() !== "" && String(value).trim() !== "—";

const SectionTitle = ({ children }) => (
  <Typography
    variant="overline"
    color="text.secondary"
    sx={{ fontWeight: 700, letterSpacing: 0.6, display: "block", mb: 1 }}
  >
    {children}
  </Typography>
);

SectionTitle.propTypes = {
  children: PropTypes.node,
};

const DetailItem = ({ label, value, fullWidth }) => {
  if (!hasText(value) && value !== 0) return null;
  return (
    <Grid item xs={12} sm={fullWidth ? 12 : 6}>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.25, lineHeight: 1.3 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
        {value}
      </Typography>
    </Grid>
  );
};

DetailItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullWidth: PropTypes.bool,
};

const KpiCard = ({ label, children, dominant, minWidth = 120 }) => (
  <Paper
    variant="outlined"
    sx={{
      p: dominant ? 1.5 : 1.25,
      minWidth,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      borderWidth: dominant ? 2 : 1,
      borderColor: dominant ? "primary.main" : "divider",
      bgcolor: dominant ? "action.hover" : "background.paper",
    }}
  >
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ mb: 0.5, lineHeight: 1.2 }}
    >
      {label}
    </Typography>
    <Box>{children}</Box>
  </Paper>
);

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
  dominant: PropTypes.bool,
  minWidth: PropTypes.number,
};

const CollapsibleSection = ({ title, summary, children }) => (
  <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 0.75 } }}
    >
      <Box sx={{ minWidth: 0, pr: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
          {title}
        </Typography>
        {summary ? (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block"
          >
            {summary}
          </Typography>
        ) : null}
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0, pb: 1.5, px: 2 }}>
      <Grid container spacing={1.5}>
        {children}
      </Grid>
    </AccordionDetails>
  </Accordion>
);

CollapsibleSection.propTypes = {
  title: PropTypes.string.isRequired,
  summary: PropTypes.string,
  children: PropTypes.node,
};

const collectFiles = (row) => {
  const candidates = [
    { label: "محل نصب ۱", url: row.plantInstallImage1Url },
    { label: "محل نصب ۲", url: row.plantInstallImage2Url },
    { label: "قبض برق", url: row.electricBoillUrl },
    { label: "سند مالکیت", url: row.homeOwnerShipDocUrl },
  ];
  return candidates.filter((item) => hasText(item.url));
};

const buildRowData = (row) => {
  if (!row) return null;

  const formatDate = (value) => (value ? toJalaliDate(value) : null);
  const areaDisplay =
    row.installableAreaSquareMeter != null
      ? `${row.installableAreaSquareMeter} m²`
      : null;

  const followUpCount = row.followUpCount ?? 0;
  const hasFollowUp =
    followUpCount > 0 ||
    row.lastFollowUpDate ||
    row.nextFollowUpDate ||
    (hasText(row.followUpReminderTitle) &&
      row.followUpReminderTitle !== "بدون پیگیری");

  const hasOwnership =
    (row.assignedOperatorUserId &&
      row.assignedOperatorDisplay !== "بدون مسئول") ||
    hasText(row.assignmentSourceTitle) ||
    row.assignedAt;

  const hasLeadDetails =
    hasText(row.applicantGoalsTitle) || hasText(row.leadScoreReasonSummary);

  const identity = getApplicantIdentityFields(row);
  const resolvedMobile = resolveApplicantMobile(row);
  const identityFields = [
    { label: "نام", value: row.firstName || row.applicantFullName },
    { label: "نام خانوادگی", value: row.lastName },
    { label: "کد ملی", value: identity.nationalCode },
    { label: "نام پدر", value: identity.fatherName },
    { label: "تاریخ تولد", value: identity.birthDateTitle },
    { label: "جنسیت", value: identity.genderTitle },
    { label: "نام کاربری", value: identity.userName },
    {
      label: "همراه",
      value: row.mobile || (resolvedMobile !== "—" ? resolvedMobile : ""),
    },
    { label: "ایمیل", value: identity.email },
    { label: "تلفن ثابت", value: identity.telephon },
    { label: "نوع شخص", value: identity.agreementKindTitle },
  ];

  const plantFields = [
    { label: "نوع نیروگاه", value: row.powerPlantTypeTitle },
    { label: "کاربری محل", value: row.locationUsageTypeTitle },
    { label: "مساحت قابل نصب", value: areaDisplay },
    { label: "وضعیت مالکیت", value: row.ownershipStatusTitle },
    { label: "ظرفیت", value: row.plantCapacityTitle },
    { label: "شهرستان", value: row.cityName },
    { label: "مرحله فعلی", value: row.stepTitle },
    { label: "وضعیت درخواست", value: row.requestConfirmTitle },
  ].filter((f) => hasText(f.value));

  return {
    formatDate,
    followUpCount,
    hasFollowUp,
    hasOwnership,
    hasLeadDetails,
    identityFields,
    files: collectFiles(row),
    plantFields,
    operationStatus: row.canClaim
      ? "قابل دریافت"
      : row.canRelease
        ? "قابل آزادسازی"
        : null,
  };
};

const CheckNewRequestMoreInfoModal = memo(
  ({
    open,
    onClose,
    row,
    canEdit = false,
    showAssignmentUi = false,
    assignmentBusyId = null,
    onClaim,
    onRelease,
    onFollowUp,
  }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const fileBaseUrl = useMemo(() => getApiOrigin(), []);
    const data = useMemo(() => buildRowData(row), [row]);

    const showClaim =
      canShowClaimAction(row, showAssignmentUi) && assignmentBusyId !== row?.id;
    const showRelease =
      showAssignmentUi && row?.canRelease && assignmentBusyId !== row?.id;
    const showFollowUp = canEdit && onFollowUp;
    const showFooter = showClaim || showRelease || showFollowUp;

    if (!row || !data) return null;

    const priorityChip = (
      <Chip
        label={row.leadPriorityTitle || "—"}
        size="small"
        sx={
          row.leadPriorityColor
            ? {
                bgcolor: row.leadPriorityColor,
                color: "#fff",
                fontWeight: 700,
                maxWidth: "100%",
              }
            : { fontWeight: 700, maxWidth: "100%" }
        }
      />
    );

    const reminderChip = (
      <Chip
        label={row.followUpReminderTitle || "بدون پیگیری"}
        size="small"
        sx={
          row.followUpReminderColor
            ? {
                bgcolor: row.followUpReminderColor,
                color: "#fff",
                fontWeight: 600,
                maxWidth: "100%",
              }
            : { maxWidth: "100%" }
        }
      />
    );

    return (
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={ModalTransition}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: isMobile ? "100%" : "90vh",
            display: "flex",
            flexDirection: "column",
            m: isMobile ? 0 : 2,
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 2, sm: 2.5 },
            pt: 2,
            pb: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="h6" fontWeight={800} noWrap>
                {row.applicantFullName || "—"}
              </Typography>
              {row.referenceID ? (
                <Typography variant="caption" color="text.secondary">
                  شناسه {row.referenceID}
                </Typography>
              ) : null}
            </Box>
            <IconButton aria-label="بستن" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Box
            sx={{
              mt: 1.5,
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(4, minmax(140px, 1fr))",
              },
              gap: 1.5,
              alignItems: "stretch",
            }}
          >
            <KpiCard label="اولویت پیگیری" dominant minWidth={0}>
              {priorityChip}
            </KpiCard>
            <KpiCard label="امتیاز" dominant minWidth={0}>
              <Typography
                variant="h4"
                component="span"
                color="primary.main"
                fontWeight={900}
                lineHeight={1}
              >
                {row.leadScore ?? 0}
              </Typography>
            </KpiCard>
            <KpiCard label="مسئول" minWidth={0}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.assignedOperatorDisplay || "بدون مسئول"}
              </Typography>
            </KpiCard>
            <KpiCard label="وضعیت پیگیری" minWidth={0}>
              {reminderChip}
            </KpiCard>
          </Box>
        </Box>

        <DialogContent
          dividers
          sx={{
            flex: 1,
            overflowY: "auto",
            px: { xs: 2, sm: 2.5 },
            py: 2,
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 2 }}>
                <SectionTitle>اطلاعات هویتی متقاضی</SectionTitle>
                <Grid container spacing={1.5}>
                  {data.identityFields.map((field) => (
                    <DetailItem
                      key={field.label}
                      label={field.label}
                      value={
                        hasText(field.value) || field.value === 0
                          ? field.value
                          : "—"
                      }
                    />
                  ))}
                </Grid>
              </Box>

              {data.plantFields.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <SectionTitle>اطلاعات نیروگاه</SectionTitle>
                  <Grid container spacing={1.5}>
                    {data.plantFields.map((field) => (
                      <DetailItem
                        key={field.label}
                        label={field.label}
                        value={field.value}
                      />
                    ))}
                  </Grid>
                </Box>
              ) : null}

              {data.hasFollowUp ? (
                <CollapsibleSection
                  title="اطلاعات پیگیری"
                  summary={
                    data.followUpCount > 0
                      ? `${data.followUpCount} پیگیری ثبت‌شده`
                      : row.followUpReminderTitle
                  }
                >
                  <DetailItem label="تعداد پیگیری" value={data.followUpCount} />
                  <DetailItem
                    label="آخرین پیگیری"
                    value={data.formatDate(row.lastFollowUpDate)}
                  />
                  <DetailItem
                    label="نتیجه آخرین پیگیری"
                    value={row.lastFollowUpResultTitle}
                  />
                  <DetailItem
                    label="پیگیری بعدی"
                    value={data.formatDate(row.nextFollowUpDate)}
                  />
                </CollapsibleSection>
              ) : null}

              {data.hasLeadDetails ? (
                <CollapsibleSection title="جزئیات امتیاز">
                  <DetailItem
                    label="هدف متقاضی"
                    value={row.applicantGoalsTitle}
                    fullWidth
                  />
                  <DetailItem
                    label="دلیل امتیاز"
                    value={row.leadScoreReasonSummary}
                    fullWidth
                  />
                </CollapsibleSection>
              ) : null}
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack spacing={1.5}>
                {data.hasOwnership ? (
                  <CollapsibleSection
                    title="مسئولیت"
                    summary={row.assignedOperatorDisplay}
                  >
                    <DetailItem
                      label="مسئول فعلی"
                      value={row.assignedOperatorDisplay}
                      fullWidth
                    />
                    <DetailItem
                      label="منبع تخصیص"
                      value={row.assignmentSourceTitle}
                      fullWidth
                    />
                    <DetailItem
                      label="زمان تخصیص"
                      value={data.formatDate(row.assignedAt)}
                      fullWidth
                    />
                    {data.operationStatus ? (
                      <DetailItem
                        label="وضعیت عملیات"
                        value={data.operationStatus}
                        fullWidth
                      />
                    ) : null}
                  </CollapsibleSection>
                ) : null}

                {data.files.length > 0 ? (
                  <Accordion disableGutters elevation={0} sx={ACCORDION_SX}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minHeight: 48,
                        "& .MuiAccordionSummary-content": { my: 0.75 },
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <ImageOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="subtitle2" fontWeight={700}>
                          فایل‌ها و تصاویر ({data.files.length})
                        </Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
                      <FileThumbnailGrid
                        files={data.files}
                        fileBaseUrl={fileBaseUrl}
                      />
                    </AccordionDetails>
                  </Accordion>
                ) : null}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>

        {showFooter ? (
          <DialogActions
            sx={{
              flexShrink: 0,
              px: 2.5,
              py: 1.5,
              gap: 1,
              borderTop: 1,
              borderColor: "divider",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            {showClaim ? (
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<PersonAddAlt1Icon />}
                onClick={() => onClaim?.(row)}
              >
                دریافت درخواست
              </Button>
            ) : null}
            {showRelease ? (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<LockOpenIcon />}
                onClick={() => onRelease?.(row)}
              >
                آزادسازی
              </Button>
            ) : null}
            {showFollowUp ? (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<EditNoteIcon />}
                onClick={() => {
                  onFollowUp?.(row);
                  onClose();
                }}
              >
                ثبت پیگیری
              </Button>
            ) : null}
          </DialogActions>
        ) : null}
      </Dialog>
    );
  }
);

CheckNewRequestMoreInfoModal.displayName = "CheckNewRequestMoreInfoModal";

CheckNewRequestMoreInfoModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  row: PropTypes.object,
  canEdit: PropTypes.bool,
  showAssignmentUi: PropTypes.bool,
  assignmentBusyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClaim: PropTypes.func,
  onRelease: PropTypes.func,
  onFollowUp: PropTypes.func,
};

export default CheckNewRequestMoreInfoModal;
